import type { ReactiveController, ReactiveControllerHost } from 'lit';

/** "Kurz sehen" pause (Anke, 2026-09-09) before the dissolve animation
 *  itself starts — the row stays exactly as freshly reloaded (showing its
 *  new "Done" status) for a beat, so the transition reads as "this ticket
 *  just got marked done", not as a random disappearance. */
const VISIBLE_BEFORE_DISSOLVE_MS = 700;

/** How long the CSS dissolve transition itself runs — must match
 *  `.my-organizer-*__row--dissolving`'s `transition` duration in
 *  `my-organizer.css`, since this is what actually removes the row afterwards. */
const DISSOLVE_ANIMATION_MS = 500;

/**
 * "Dissolve and remove" animation (Anke, 2026-09-09: "wenn ein Ticket im My
 * Organizer-Modus auf done gesetzt wird, dann würde ich gern, dass man nach
 * Schließen des Issue-Edit-Dialoges das Ticket noch kurz sieht und es dann
 * mit einer Auflöseanimation aus der Box oder Kalenderview verschwindet") —
 * used by `<my-organizer-label-column>` and `<my-organizer-calendar-day>`
 * together with `diffDoneRows()` (`domain/helpers/diff-done-rows.helper.ts`):
 * that helper decides WHICH rows just went DONE, this controller decides
 * WHEN/HOW they visually leave.
 *
 * Unlike `JustMovedFlashController` (one flash at a time — drag&drop is a
 * single sequential user action), several rows can go DONE independently of
 * each other (e.g. after being away for a while), so this tracks a whole SET
 * of in-flight dissolves, each keyed by its own Issue uid, each with its own
 * two-phase timeline: `isDissolving(uid)` is false during the initial
 * "kurz sehen" pause, then true for `DISSOLVE_ANIMATION_MS` (drives the CSS
 * fade+shrink), then `onComplete()` fires — the CALLER removes the row from
 * its own `_rows` at that point (this controller holds no row data itself,
 * only timing state), mirroring how `JustMovedFlashController` never touches
 * `_issues`/`_rows` either.
 *
 * A plain private class field, NOT `@state()` — same reasoning as
 * `JustMovedFlashController`: not a Lit element itself, calls
 * `host.requestUpdate()` explicitly instead.
 */
export class DissolveFlashController implements ReactiveController {

    private readonly _dissolving = new Set<string>();
    private readonly _timeoutIds = new Map<string, [ReturnType<typeof setTimeout>, ReturnType<typeof setTimeout>]>();

    constructor(private readonly host: ReactiveControllerHost) {
        host.addController(this);
    }

    /** Required by `ReactiveController` — no lifecycle hookup needed on connect itself. */
    public hostConnected(): void {
        // no-op
    }

    /** Cancels every in-flight dissolve's timers on disconnect — a
     *  reconnected host should not resume mid-dissolve for a row it may not
     *  even show anymore, and a torn-down host must never have `onComplete`
     *  fire against it afterwards. */
    public hostDisconnected(): void {
        for (const [visibleTimeout, dissolveTimeout] of this._timeoutIds.values()) {
            clearTimeout(visibleTimeout);
            clearTimeout(dissolveTimeout);
        }
        this._timeoutIds.clear();
        this._dissolving.clear();
    }

    /**
     * True for the ENTIRE dissolve timeline — the initial "kurz sehen"
     * pause AND the CSS animation phase, false only once `onComplete` has
     * fired (or nothing was ever started for this uid). Used by
     * `<my-organizer-assigned-column>` (Anke, 2026-09-09, follow-up: "auch
     * in der Inbox") to know a row it can no longer re-fetch (its own
     * `findAllAssignedToMe()` already excludes DONE server-side) is still
     * mid-dissolve and must stay in `_issues` a little longer — see
     * `isDissolving()` below for the animation-only subset of this.
     */
    public has(issueUid: string): boolean {
        return this._timeoutIds.has(issueUid);
    }

    /** True only during the CSS-animated fade+shrink phase — false during
     *  the initial "kurz sehen" pause and once it's finished. */
    public isDissolving(issueUid: string): boolean {
        return this._dissolving.has(issueUid);
    }

    /**
     * Starts (or restarts, if already running for this uid) the dissolve
     * timeline for `issueUid`. `onComplete` is called exactly once, after
     * the CSS animation's own duration has elapsed — the caller uses it to
     * actually drop the row from its data.
     */
    public start(issueUid: string, onComplete: () => void): void {
        this._cancel(issueUid);

        const visibleTimeout = setTimeout(() => {
            this._dissolving.add(issueUid);
            this.host.requestUpdate();

            const dissolveTimeout = setTimeout(() => {
                this._dissolving.delete(issueUid);
                this._timeoutIds.delete(issueUid);
                onComplete();
            }, DISSOLVE_ANIMATION_MS);

            this._timeoutIds.set(issueUid, [visibleTimeout, dissolveTimeout]);
        }, VISIBLE_BEFORE_DISSOLVE_MS);

        this._timeoutIds.set(issueUid, [visibleTimeout, visibleTimeout]);
    }

    private _cancel(issueUid: string): void {
        const existing = this._timeoutIds.get(issueUid);
        if (existing) {
            clearTimeout(existing[0]);
            clearTimeout(existing[1]);
            this._timeoutIds.delete(issueUid);
        }
        this._dissolving.delete(issueUid);
    }
}
